import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Trash2, Loader2, CheckCircle, XCircle, EyeOff, RotateCcw,
  Eye, Edit, ShieldCheck, ShieldOff, Plus, LogIn, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_BG = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const formatLastUpdated = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http")) return logo;
  return `${BACKEND_URL}${logo}`;
};

export const MarketManagement = ({ markets, token, onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [subFilter, setSubFilter] = useState("all");
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const handleMarketAction = async (marketId, action) => {
    setUpdatingStatus(marketId);
    try {
      await axios.post(`${API}/admin/market/${marketId}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Market ${action}d successfully`);
      onRefresh();
    } catch { toast.error(`Failed to ${action} market`); }
    finally { setUpdatingStatus(null); }
  };

  const handleDeleteMarket = async (marketId) => {
    try {
      await axios.delete(`${API}/admin/market/${marketId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Market deleted");
      onRefresh();
    } catch { toast.error("Failed to delete market"); }
  };

  const handleToggleOverride = async (marketId) => {
    setUpdatingStatus(marketId);
    try {
      const r = await axios.post(`${API}/admin/market/${marketId}/toggle-override`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.admin_subscription_override ? "Subscription override enabled — listing now visible" : "Subscription override removed");
      onRefresh();
    } catch { toast.error("Failed to toggle override"); }
    finally { setUpdatingStatus(null); }
  };

  const openProfile = (market) => navigate(`/admin/market/${market.id}`);

  const handleLoginAsMarket = async (market) => {
    try {
      const response = await axios.post(`${API}/admin/auth/login-as`, {
        email: market.email,
        account_id: market.id,
        user_type: "market",
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem("bizkeep_token", response.data.access_token);
      localStorage.setItem("bizkeep_email", response.data.email);
      localStorage.setItem("bizkeep_user_type", "market");
      toast.success(`Logged in as ${market.market_name || market.email}`);
      window.open("/market-dashboard", "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login as market");
    }
  };

  const filteredMarkets = markets.filter(m => {
    const matchesSearch = !searchTerm ||
      m.market_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email?.toLowerCase().includes(searchTerm.toLowerCase());
    if (subFilter === "no_sub") return matchesSearch && !m.subscription_active && !m.admin_subscription_override;
    if (subFilter === "active") return matchesSearch && (m.subscription_active || m.admin_subscription_override);
    return matchesSearch;
  });

  const noSubCount = markets.filter(m => !m.subscription_active && !m.admin_subscription_override).length;

  return (
    <div>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Manage Markets</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <Button onClick={() => navigate("/admin/market/new")} className="bg-[#1a1a1a] hover:bg-slate-800 text-white" data-testid="admin-add-market-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Market
          </Button>
          <div className="flex gap-1 bg-gray-100 rounded-full p-1">
            {[
              { v: "all", l: `All (${markets.length})` },
              { v: "active", l: "Active" },
              { v: "no_sub", l: `No Sub (${noSubCount})` },
            ].map((opt) => (
              <button key={opt.v} onClick={() => setSubFilter(opt.v)} className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${subFilter === opt.v ? "bg-white shadow text-[#1a1a1a]" : "text-gray-600 hover:text-[#1a1a1a]"}`} data-testid={`market-sub-filter-${opt.v}`}>{opt.l}</button>
            ))}
          </div>
          <div className="relative flex-1 md:w-64 md:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input type="text" placeholder="Search markets..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-10 border-gray-200" data-testid="admin-market-search" />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {filteredMarkets.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No markets yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Market Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Subscription</TableHead>
                <TableHead className="font-semibold">Approval</TableHead>
                <TableHead className="font-semibold">Last Updated</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMarkets.map((market) => (
                <TableRow key={market.id} className="hover:bg-gray-50">
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => openProfile(market)}
                      className="text-left font-medium text-slate-900 hover:text-[#42D78A] transition-colors"
                      data-testid={`admin-edit-market-name-${market.id}`}
                    >
                      {market.market_name || "Unnamed"}
                    </button>
                  </TableCell>
                  <TableCell className="text-gray-600">{market.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {market.subscription_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                      ) : market.admin_subscription_override ? (
                        <Badge className="bg-blue-100 text-blue-800">Override</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">No Sub</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={market.approval_status === "pending" ? "bg-yellow-100 text-yellow-800" : market.approval_status === "rejected" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                        {market.approval_status || "approved"}
                      </Badge>
                      {market.profile_review_needed && (
                        <Badge className="bg-amber-100 text-amber-800">
                          <AlertTriangle className="mr-1 h-3 w-3" /> Edited
                        </Badge>
                      )}
                      {market.is_delisted && <Badge className="bg-orange-100 text-orange-800">Delisted</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{formatLastUpdated(market.updated_at || market.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openProfile(market)} className="text-slate-600 hover:text-slate-900" data-testid={`admin-view-market-${market.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {market.approval_status === "pending" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleMarketAction(market.id, "approve")} disabled={updatingStatus === market.id} className="text-green-600 hover:bg-green-50" data-testid={`admin-approve-market-${market.id}`}>
                            {updatingStatus === market.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleMarketAction(market.id, "reject")} disabled={updatingStatus === market.id} className="text-red-500 hover:bg-red-50" data-testid={`admin-reject-market-${market.id}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {market.approval_status === "rejected" && (
                        <Button variant="ghost" size="sm" onClick={() => handleMarketAction(market.id, "approve")} className="text-green-600 hover:bg-green-50"><CheckCircle className="w-4 h-4" /></Button>
                      )}
                      {(market.approval_status === "approved" || !market.approval_status) && (
                        <Button variant="ghost" size="sm" onClick={() => handleMarketAction(market.id, market.is_delisted ? "relist" : "delist")} className={market.is_delisted ? "text-green-600 hover:bg-green-50" : "text-orange-500 hover:bg-orange-50"} title={market.is_delisted ? "Relist" : "Delist"}>
                          {market.is_delisted ? <RotateCcw className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                      )}
                      {(market.approval_status === "approved" || !market.approval_status) && !market.subscription_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleOverride(market.id)}
                          disabled={updatingStatus === market.id}
                          className={market.admin_subscription_override ? "text-blue-600 hover:bg-blue-50" : "text-amber-600 hover:bg-amber-50"}
                          title={market.admin_subscription_override ? "Remove subscription override" : "Override subscription requirement (force visible)"}
                          data-testid={`admin-toggle-override-market-${market.id}`}
                        >
                          {updatingStatus === market.id ? <Loader2 className="w-4 h-4 animate-spin" /> : market.admin_subscription_override ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openProfile(market)} className="text-slate-600 hover:text-slate-900" data-testid={`admin-edit-market-${market.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleLoginAsMarket(market)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Login as market" data-testid={`admin-login-as-market-${market.id}`}>
                        <LogIn className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteMarket(market.id)} className="text-red-500 hover:bg-red-50" data-testid={`admin-delete-market-${market.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
